"use client";

import React, { useState } from "react";
import { ShieldCheck, FileText, SlidersHorizontal } from "lucide-react";
import { Link } from "@/components/common/Link";
import Reveal from "@/components/common/Reveal";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { useLanguage } from "@/hooks/useLanguage";
import { pick, type Lang } from "@/lib/i18n";
interface ChemicalEnvironment {
  id: string;
  category: Record<Lang, string>;
  medium: Record<Lang, string>;
  concentration: string;
  recommendedAlloy: string;
  alternateAlloy: string;
  notes: Record<Lang, string>;
}
const chemicalEnvironments: ChemicalEnvironment[] = [{
  id: "water-neutral",
  category: {
    en: "Aqueous & Neutral",
    zh: "中性水溶液与常温清洗",
    es: "Acuoso y neutro",
    de: "Wässrig & neutral",
    fr: "Aqueux et neutre",
    pt: "Aquoso e neutro"
  },
  medium: {
    en: "Potable water, food slurries & mild steam",
    zh: "饮用水、食品料浆与常压蒸汽",
    es: "Agua potable, pastas alimentarias y vapor suave",
    de: "Trinkwasser, Lebensmittelsuspensionen & Niederdruckdampf",
    fr: "Eau potable, bouillies alimentaires et vapeur basse pression",
    pt: "Água potável, pastas alimentícias e vapor suave"
  },
  concentration: "pH 6.5 – 8.5",
  recommendedAlloy: "AISI 304",
  alternateAlloy: "AISI 304L",
  notes: {
    en: "Standard economical grade for non-chloride aqueous separation and food screening.",
    zh: "无氯化物中性水溶液介质及食品工业初级筛分的高性价比基准材质。",
    es: "Grado estándar económico para separación acuosa sin cloruros y cribado de alimentos.",
    de: "Wirtschaftlicher Standardwerkstoff für chloridfreie wässrige Trennung und Lebensmittelsiebung.",
    fr: "Nuance standard économique pour la séparation aqueuse sans chlorure et le tamisage alimentaire.",
    pt: "Grau padrão econômico para separação aquosa sem cloretos e peneiramento alimentício."
  }
}, {
  id: "marine-chloride",
  category: {
    en: "Marine & Chloride",
    zh: "含氯水体与海洋盐雾",
    es: "Marino y cloruros",
    de: "Marine & Chloride",
    fr: "Marin et chlorures",
    pt: "Marinho e cloretos"
  },
  medium: {
    en: "Brine, brackish water & organic acids",
    zh: "浓盐水、微咸水与有机弱酸",
    es: "Salmuera, agua salobre y ácidos orgánicos",
    de: "Sole, Brackwasser & organische Säuren",
    fr: "Saumure, eau saumâtre et acides organiques",
    pt: "Salmoura, água salobra e ácidos orgânicos"
  },
  concentration: "Up to 3.5% NaCl",
  recommendedAlloy: "AISI 316L",
  alternateAlloy: "AISI 316",
  notes: {
    en: "Molybdenum addition (2-3%) provides critical pitting and crevice corrosion resistance.",
    zh: "2-3% 钼元素增强基体抗氯离子点蚀与微孔缝隙腐蚀性能。",
    es: "La adición de molibdeno (2-3%) proporciona resistencia crítica a la corrosión por picaduras y grietas.",
    de: "Molybdänzusatz (2-3%) bietet entscheidenden Schutz gegen Lochfraß- und Spaltkorrosion.",
    fr: "L'ajout de molybdène (2-3%) offre une résistance essentielle contre les piqûres et fissures.",
    pt: "A adição de molibdênio (2-3%) confere resistência crítica à corrosão por pites e frestas."
  }
}, {
  id: "high-temperature",
  category: {
    en: "Thermal Processing",
    zh: "高温氧化与窑炉热风",
    es: "Procesamiento térmico",
    de: "Thermische Prozesse",
    fr: "Procédés thermiques",
    pt: "Processamento térmico"
  },
  medium: {
    en: "Hot flue gases, calciner exhaust & kiln atmospheres",
    zh: "高温窑炉烟气、焚烧废气与催化煅烧气体",
    es: "Gases de combustión calientes, escape de calcinadores y hornos",
    de: "Heiße Rauchgase, Kalzinierabgase & Ofenatmosphären",
    fr: "Gaz de combustion chauds, effluents de calcination et atmosphères de fours",
    pt: "Gases de combustão quentes, exaustão de calcinadores e fornos"
  },
  concentration: "Oxidizing / Inert",
  recommendedAlloy: "AISI 314",
  alternateAlloy: "AISI 310S",
  notes: {
    en: "High silicon and chromium-nickel content prevents scaling in continuous thermal duties.",
    zh: "高硅与高铬镍配比有效抑制持续高温气氛下的氧化剥落并维持网孔结构强度。",
    es: "Alto contenido de silicio y cromo-níquel previene la oxidación continua a alta temperatura.",
    de: "Hoher Silizium- und Chrom-Nickel-Gehalt verhindert Verzunderung bei Dauerbetrieb.",
    fr: "Forte teneur en silicium et chrome-nickel empêchant l'écaillage en service thermique continu.",
    pt: "Alto teor de silício e cromo-níquel impede a carepa sob operação térmica contínua."
  }
}, {
  id: "severe-chemical",
  category: {
    en: "Aggressive Acids & Halides",
    zh: "严苛无机酸与湿氯气",
    es: "Ácidos severos y haluros",
    de: "Aggressive Säuren & Halogenide",
    fr: "Acides sévères et halogénures",
    pt: "Ácidos severos e haletos"
  },
  medium: {
    en: "Wet chlorine, hydrochloric acid vapors & ferric chloride",
    zh: "湿氯气、盐酸酸雾与强腐蚀性氯化铁溶液",
    es: "Cloro húmedo, vapores de ácido clorhídrico y cloruro férrico",
    de: "Feuchtes Chlorgas, Salzsäuredämpfe & Eisen(III)-chlorid",
    fr: "Chlore gazeux humide, vapeurs d'acide chlorhydrique et chlorure ferrique",
    pt: "Cloro úmido, vapores de ácido clorídrico e cloreto férrico"
  },
  concentration: "Severe Acidic",
  recommendedAlloy: "Hastelloy C-276",
  alternateAlloy: "Nickel Alloys",
  notes: {
    en: "Tungsten and molybdenum additions deliver extreme resistance to oxidizing and reducing media.",
    zh: "钨和高钼超合金配方耐受极强氧化性与强还原性复杂混合酸介质。",
    es: "Adiciones de tungsteno y molibdeno brindan resistencia extrema a medios oxidantes y reductores.",
    de: "Wolfram- und Molybdänzusätze bieten extremen Schutz in oxidierenden und reduzierenden Medien.",
    fr: "Les ajouts de tungstène et molybdène procurent une résistance extrême aux milieux oxydants et réducteurs.",
    pt: "Adições de tungstênio e molibdênio garantem resistência extrema a meios oxidantes e redutores."
  }
}];
const copy = {
  sectionBadge: {
    en: "Operating Media Matrix",
    zh: "介质工况选型矩阵",
    es: "Matriz de medios operativos",
    de: "Prozessmedien-Matrix",
    fr: "Matrice des milieux de procédé",
    pt: "Matriz de meios operacionais"
  },
  sectionTitle: {
    en: "Wire mesh alloy suitability by operating environment",
    zh: "按工况化学介质细分的不锈钢丝网材质适配指南",
    es: "Idoneidad de aleaciones de malla según el entorno operativo",
    de: "Drahtgewebe-Werkstoffeignung nach Einsatzumgebung",
    fr: "Adéquation des alliages de toile selon l'environnement opérationnel",
    pt: "Adequação de ligas de tela metálica por ambiente operacional"
  },
  sectionSubtitle: {
    en: "Review recommended stainless and nickel alloy grades based on exposure chemistry, chloride presence, and operating temperature thresholds.",
    zh: "根据工况介质成分、氯离子浓度与工作温度上限对比推荐的奥氏体不锈钢与镍基合金材质方案。",
    es: "Consulte los grados de acero inoxidable y aleaciones de níquel recomendados según la química de exposición, la presencia de cloruros y los umbrales térmicos.",
    de: "Empfohlene Edelstahl- und Nickelbasislegierungen basierend auf Prozesschemie, Chloridgehalt und Temperatureinsatzgrenzen im Vergleich.",
    fr: "Consultez les nuances d'inox et d'alliages de nickel recommandées selon la chimie d'exposition, la présence de chlorures et les seuils thermiques.",
    pt: "Consulte as ligas de aço inoxidável e níquel recomendadas com base na agressividade química, presença de cloretos e limites térmicos."
  },
  mediumLabel: {
    en: "Process medium:",
    zh: "工艺介质:",
    es: "Medio de proceso:",
    de: "Prozessmedium:",
    fr: "Milieu du procédé :",
    pt: "Meio do processo:"
  },
  concentrationLabel: {
    en: "Concentration / pH:",
    zh: "介质浓度 / pH 范围:",
    es: "Concentración / pH:",
    de: "Konzentration / pH:",
    fr: "Concentration / pH :",
    pt: "Concentração / pH:"
  },
  recommendedLabel: {
    en: "Recommended Grade",
    zh: "推荐标准材质",
    es: "Grado recomendado",
    de: "Empfohlener Werkstoff",
    fr: "Nuance recommandée",
    pt: "Grau recomendado"
  },
  alternateLabel: {
    en: "Alternate / Upgraded",
    zh: "备选 / 升级牌号",
    es: "Alternativa / superior",
    de: "Alternative / Upgrade",
    fr: "Alternative / supérieure",
    pt: "Alternativa / aprimorada"
  },
  purityNotice: {
    en: "Mesh aperture stability depends on proper corrosion allowance. In borderline environments, contact our engineering team with your fluid chemistry details.",
    zh: "精密丝网开孔孔径的长期稳定性直接取决于基体耐腐蚀裕量。如工况处于临界介质环境，请提供工艺流体成分以获得选型支持。",
    es: "La estabilidad de la abertura de la malla depende de un margen adecuado de corrosión. En entornos límite, consulte a nuestro equipo de ingeniería con los detalles de su fluido.",
    de: "Die Maßhaltigkeit der Maschenweite erfordert ausreichende Korrosionsbeständigkeit. Bei Grenzfällen berät Sie unser Fachteam anhand Ihrer Fluidanalyse.",
    fr: "La stabilité de l'ouverture dépend d'une surépaisseur de corrosion adéquate. En conditions limites, transmettez vos données de fluide à notre équipe technique.",
    pt: "A estabilidade da abertura da tela depende de margem de corrosão adequada. Em condições limítrofes, consulte nossa engenharia com os dados do fluido."
  },
  technicalGuideLink: {
    en: "View stainless steel grade selection guide",
    zh: "查阅不锈钢牌号选型技术指南",
    es: "Ver guía de selección de grados de acero inoxidable",
    de: "Leitfaden zur Edelstahlgüten-Auswahl ansehen",
    fr: "Consulter le guide de sélection des nuances d'inox",
    pt: "Ver guia de seleção de graus de aço inoxidável"
  }
};
export default function Compatibility() {
  const {
    lang
  } = useLanguage();
  const [activeTab, setActiveTab] = useState(chemicalEnvironments[0].id);
  const selectedEnv = chemicalEnvironments.find(e => e.id === activeTab) || chemicalEnvironments[0];
  return <section id="compatibilitymatrix" data-nav="light" className="bg-background text-foreground mf-section border-t border-border">
      <div className="mf-container">
        <Reveal delay={0}>
          <div className="max-w-3xl mb-10">
            <div className="inline-flex items-center gap-2 mb-3">
              <Badge variant="outline" className="gap-1.5 rounded-md border-border bg-card px-3 py-1 font-mono mf-caption tracking-tight text-foreground">
                <SlidersHorizontal className="size-3.5 text-primary" aria-hidden="true" />
                <span>{pick(lang, copy.sectionBadge)}</span>
              </Badge>
            </div>

            <h2 className="font-default text-balance mf-h2 text-foreground">
              {pick(lang, copy.sectionTitle)}
            </h2>

            <p className="mt-3 text-muted-foreground mf-lead">
              {pick(lang, copy.sectionSubtitle)}
            </p>
          </div>
        </Reveal>

        {/* Environment selector tabs */}
        <Reveal delay={40}>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-8">
            {chemicalEnvironments.map(env => {
            const isActive = env.id === activeTab;
            return <button key={env.id} type="button" onClick={() => setActiveTab(env.id)} className={`p-3 text-left rounded-lg border transition-[background-color,border-color] flex flex-col gap-1 ${isActive ? "border-primary bg-primary/5 text-foreground" : "border-border bg-card text-muted-foreground hover:bg-muted/50 hover:text-foreground"}`}>
                  <span className="mf-caption font-mono uppercase tracking-wider text-primary">
                    {env.recommendedAlloy}
                  </span>
                  <span className="text-sm font-semibold text-foreground leading-snug line-clamp-1">
                    {pick(lang, env.category)}
                  </span>
                </button>;
          })}
          </div>
        </Reveal>

        {/* Active Environment Detail Display */}
        <Reveal delay={80}>
          <Card className="overflow-hidden rounded-lg border border-border bg-card">
            <CardHeader className="border-b border-border bg-muted/30 p-5 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                  <span className="mf-caption font-mono text-primary uppercase tracking-wider">
                    {pick(lang, copy.sectionBadge)}
                  </span>
                  <CardTitle className="text-xl sm:text-2xl font-semibold text-foreground mt-1">
                    {pick(lang, selectedEnv.category)}
                  </CardTitle>
                  <CardDescription className="mf-lead text-muted-foreground mt-1">
                    {pick(lang, selectedEnv.medium)}
                  </CardDescription>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Badge variant="outline" className="font-mono mf-caption px-3 py-1 bg-card border-border">
                    {selectedEnv.concentration}
                  </Badge>
                </div>
              </div>
            </CardHeader>

            <CardContent className="p-5 sm:p-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Recommended Grade Box */}
                <div className="p-4 rounded-lg border border-border bg-background flex flex-col justify-between">
                  <div className="flex items-center justify-between mb-3">
                    <span className="mf-caption font-mono uppercase tracking-wider text-muted-foreground">
                      {pick(lang, copy.recommendedLabel)}
                    </span>
                    <Badge className="bg-primary text-primary-foreground font-mono mf-caption">
                      Standard
                    </Badge>
                  </div>
                  <div className="text-2xl sm:text-3xl font-semibold text-foreground font-default mb-2">
                    {selectedEnv.recommendedAlloy}
                  </div>
                  <p className="mf-caption text-muted-foreground leading-relaxed">
                    {pick(lang, selectedEnv.notes)}
                  </p>
                </div>

                {/* Alternate / Upgrade Box */}
                <div className="p-4 rounded-lg border border-border bg-background flex flex-col justify-between">
                  <div className="flex items-center justify-between mb-3">
                    <span className="mf-caption font-mono uppercase tracking-wider text-muted-foreground">
                      {pick(lang, copy.alternateLabel)}
                    </span>
                    <Badge variant="outline" className="font-mono mf-caption border-border">
                      Option
                    </Badge>
                  </div>
                  <div className="text-2xl sm:text-3xl font-semibold text-foreground font-default mb-2">
                    {selectedEnv.alternateAlloy}
                  </div>
                  <div className="space-y-2 mf-caption text-muted-foreground pt-1 border-t border-border">
                    <div className="flex items-center justify-between">
                      <span>{pick(lang, copy.concentrationLabel)}</span>
                      <span className="font-mono font-medium text-foreground">{selectedEnv.concentration}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Engineering callout bar */}
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-4 rounded-lg border border-border bg-muted/40">
                <div className="flex items-start gap-3">
                  <div className="inline-flex size-10 items-center justify-center rounded-md border border-border bg-muted text-primary shrink-0 mt-0.5">
                    <ShieldCheck className="size-5" aria-hidden="true" />
                  </div>
                  <p className="mf-caption text-muted-foreground leading-relaxed max-w-2xl">
                    {pick(lang, copy.purityNotice)}
                  </p>
                </div>

                <Button asChild variant="outline" size="sm" className="shrink-0 font-medium">
                  <Link to="/StainlessSteelGradeSelectionPage" className="inline-flex items-center gap-2">
                    <FileText className="size-4" aria-hidden="true" />
                    <span>{pick(lang, copy.technicalGuideLink)}</span>
                  </Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        </Reveal>
      </div>
    </section>;
}